import { useEffect, useState } from 'react';
import { redirectToSubscriptionCheckout } from '@/lib/subscriptionCheckoutRedirect';
import { Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useCreateCheckoutSessionMutation,
  useGetSubscriptionStatusQuery,
} from '@/apis/extended/subscriptionApi';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

export default function Pricing() {
  const [createCheckoutSession, { isLoading }] =
    useCreateCheckoutSessionMutation();
  const { data: subscriptionStatus } = useGetSubscriptionStatusQuery();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const flag = searchParams.get('subscription');
    if (flag === 'pending') {
      toast.message(
        'Investor request recorded as pending. Check your email — access unlocks after approval.'
      );
    } else if (flag === 'cancelled') {
      toast.message('Checkout cancelled. No charge was made.');
    } else if (flag === 'denied') {
      toast.error('Investor access was not approved. You can request again.');
    } else if (flag === 'success') {
      toast.success(
        subscriptionStatus?.investorAccessGranted
          ? 'Investor access is active.'
          : 'Checkout received. If still pending, an admin will grant access shortly.'
      );
    }
  }, [searchParams, subscriptionStatus?.investorAccessGranted]);

  const isPending = subscriptionStatus?.status === 'pending';
  const hasAccess = Boolean(subscriptionStatus?.investorAccessGranted);

  const handleSubscribe = async () => {
    try {
      setIsProcessing(true);
      const result = await createCheckoutSession({
        plan: 'investor_monthly',
        successUrl: `${window.location.origin}/profile/settings?subscription=pending`,
        cancelUrl: `${window.location.origin}/pricing?subscription=cancelled`,
      }).unwrap();

      const ok = await redirectToSubscriptionCheckout(result);
      if (!ok) setIsProcessing(false);
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to start checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="taste-page-chrome min-h-[70vh] py-16">
      <div className="container mx-auto px-4 taste-rise">
        <div className="text-center mb-12">
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[var(--primary-navy)] mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-[var(--on-surface-variant)] max-w-2xl mx-auto">
            Get access to investor loan products and tools
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto taste-pricing-grid">
          {/* Free Plan */}
          <Card>
            <CardHeader>
              <CardTitle>Free</CardTitle>
              <CardDescription>Basic home buying tools</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold mb-6">
                $0<span className="text-lg">/month</span>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Conventional, FHA, VA, USDA loans</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Loan estimates and calculations</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Real estate offer contracts</span>
                </li>
                <li className="flex items-start">
                  <span className="text-muted-foreground mr-2">✓</span>
                  <span className="text-muted-foreground">
                    Basic property management
                  </span>
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button variant="outline" className="w-full" disabled>
                Current Plan
              </Button>
            </CardFooter>
          </Card>

          {/* Investor Plan */}
          <Card className="border-primary">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Investor
                <span className="text-sm bg-primary/10 text-primary px-2 py-1 rounded">
                  Recommended
                </span>
              </CardTitle>
              <CardDescription>Access to all investor products</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold mb-6">
                $99<span className="text-lg">/month</span>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Everything in Free plan</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Bridge loans (6, 12, 18, 24 months)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>DSCR loans</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Commercial loans</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Fix and Flip loans</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Advanced investment calculators</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Priority support</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button
                className="w-full"
                onClick={
                  hasAccess ? () => navigate('/properties') : handleSubscribe
                }
                disabled={isLoading || isProcessing || isPending}
              >
                {isLoading || isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : hasAccess ? (
                  'Open investor tools'
                ) : isPending ? (
                  'Request pending approval'
                ) : (
                  'Subscribe Now'
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>

        <div className="mt-12 text-center text-[var(--on-surface-variant)]">
          <p>Investor access is granted after checkout approval.</p>
        </div>
      </div>
    </div>
  );
}
